import { SubmitHandler } from 'react-hook-form';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import { AppealsFormDrawer } from 'assets/js/pages/account-settings/account-settings/appeals/AppealsFormDrawer';
import {
    NewAppealId,
    NewAppealItem,
    SelectAppeal,
} from 'assets/js/pages/account-settings/account-settings/appeals/SelectAppeal';
import { DrawerRoute } from 'components/drawer-route';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { usePreloadAppealFromResource } from 'customHooks/usePreloadAppealFromResource';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface AppealFormValues {
    appeal: string[];
}

const CampaignAppealSettings = () => {
    const match = useRouteMatch();
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const queryClient = useQueryClient();
    const history = useHistory();

    const methods = Form.useGivebutterForm<AppealFormValues>({
        defaultValues: {
            appeal: campaign.default_appeal_id ? [campaign.default_appeal_id.toString()] : [],
        },
    });

    const { control, path, handleSubmit, setValue } = methods;
    const { mutate: submit, isPending } = useMutation({
        mutationFn: (values: AppealFormValues) => {
            const data = {
                default_appeal_id: values.appeal.length ? parseInt(values.appeal[0]) : null,
            };
            return updateCampaign({ campaignId: campaign.id, data });
        },
        onSuccess: () => {
            toast.success('Updated campaign successfully!');

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.appeals(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update campaign'),
    });

    usePreloadAppealFromResource(campaign.default_appeal);

    return (
        <>
            <Form.Provider {...methods}>
                <form onSubmit={handleSubmit(submit as SubmitHandler<AppealFormValues>)}>
                    <SettingsCard.Root>
                        <SettingsCard.LeftSide>
                            <SettingsCard.Heading>Appeals</SettingsCard.Heading>
                            <p className={'text-sm text-gray-700'}>
                                Configure the appeal code on your campaign.
                            </p>
                        </SettingsCard.LeftSide>
                        <SettingsCard.RightSide className="flex flex-col items-start gap-4">
                            <Form.Container>
                                <Form.Label name={path('appeal')}>Appeal</Form.Label>
                                <SelectAppeal
                                    control={control}
                                    name="appeal"
                                    onChangeValue={([value]) => {
                                        if (value === NewAppealId) {
                                            history.push(
                                                `/accounts/${accountId}/campaigns/${campaign.id}/settings/add-appeal`,
                                            );
                                            setValue('appeal', []);
                                        }
                                    }}
                                >
                                    <NewAppealItem />
                                </SelectAppeal>
                                <p className={'text-sm text-gray-700'}>
                                    This appeal will be automatically applied to donations from this
                                    campaign.
                                </p>
                                <Form.Error name={path('appeal')} />
                            </Form.Container>
                            <Form.SubmitButton isSubmitting={isPending}>Save</Form.SubmitButton>
                        </SettingsCard.RightSide>
                    </SettingsCard.Root>
                </form>
            </Form.Provider>
            <DrawerRoute path={`${match.path}/add-appeal`} component={AppealsFormDrawer} />
        </>
    );
};

export { CampaignAppealSettings };
